import { useLocalization } from '@lib/localization';
import { type Inspection } from '@lib/spproject/types';
import { Box, Chip, Paper, type Theme, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import useNoticeColors from '@store/hooks/useNoticeColors';
import { ListFilter } from 'lucide-react';

export interface RecentInspections {
  name: string;
  dateModified: string;
  status: Inspection.NoticeType;
}

interface RecentInspectionsProps {
  data: RecentInspections[] | undefined;
}

const RecentInspectionsList: React.FC<RecentInspectionsProps> = ({ data }) => {
  const { t, currentLanguage } = useLocalization();
  const noticeColors = useNoticeColors();

  const columns: GridColDef<RecentInspections>[] = [
    {
      field: 'name',
      sortable: false,
      headerName: t('Common_Inspections'),
      flex: 1.5,
    },
    {
      field: 'status',
      sortable: false,
      headerName: t('Common_Status'),
      headerAlign: 'left',
      align: 'left',
      width: 120,
      renderCell: (params) => {
        return (
          <Chip
            label={t(`Common_${params.row.status == 'Ok' ? 'Successful' : params.row.status}`)}
            size="small"
            sx={{
              backgroundColor: noticeColors[params.row.status],
              color: '#fff',
            }}
          />
        );
      },
    },
    {
      field: 'dateModified',
      headerName: t('Common_DateUpdated'),
      width: 180,
      align: 'left',
      headerAlign: 'left',
      sortable: false,
      valueGetter: (value, _) => {
        if (!value) return '-';

        const date = new Date(value);
        return date.toLocaleDateString(currentLanguage, {
          day: '2-digit',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      },
    }
  ];
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          px: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ListFilter size={22} />
          <Typography variant="h6" sx={{ ml: 1, fontWeight: 300 }}>
            {t('Statistic_RecentInspections')}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, overflow: 'hidden' }}>
        <DataGrid
          rows={data}
          columns={columns}
          hideFooter
          disableColumnMenu
          disableRowSelectionOnClick
          rowHeight={60}
          sx={dataGridStyles}
          getRowId={(row) => row.dateModified}
        />
      </Paper>
    </Box>
  );
};

const dataGridStyles = (theme: Theme) => ({
  height: '100%',
  width: '100%',
  backgroundColor: theme.palette.background.paper,

  '& .MuiDataGrid-columnHeader': {
    backgroundColor: theme.palette.background.paper,
  },
  '& .MuiDataGrid-cell:focus': { outline: 'none' },
  '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
  '& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
});

export default RecentInspectionsList;
